import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { NotificationsProvider, useNotifications } from "@/context/NotificationsContext";
import { ROLE_LABELS, LOGO_FULL } from "@/lib/constants";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { WhatsAppContact } from "@/components/WhatsAppContact";
import { PushToggle } from "@/components/PushToggle";
import { MobileOnboarding } from "@/components/MobileOnboarding";
import { forgetPush, setBadge } from "@/lib/push";
import {
  LayoutDashboard, Home, CalendarDays, ListChecks, Users as UsersIcon,
  LogOut, Menu, X, CalendarCheck, LayoutTemplate, KeyRound, MessageCircle, Flame, Receipt, Shirt,
  Globe,
} from "lucide-react";
import { toast } from "sonner";

import { tx, lz, getLang, UI_LANGS } from "@/i18n";
const NAV = lz([
  { to: "/app", label: "Dashboard", id: "dashboard", icon: LayoutDashboard, end: true, roles: "all" },
  { to: "/app/houses", label: "Woningen", id: "woningen", icon: Home, roles: "all" },
  { to: "/app/reservations", label: "Boekingen", id: "boekingen", icon: CalendarCheck, roles: ["admin", "client", "guest"] },
  { to: "/app/calendar", label: "Agenda", id: "agenda", icon: CalendarDays, roles: ["admin", "host", "cleaner", "subcontractor"] },
  { to: "/app/chat", label: "Live chat", id: "live chat", icon: MessageCircle, roles: ["admin", "host", "client", "cleaner", "subcontractor"] },
  { to: "/app/tasks", label: "Taken", id: "taken", icon: ListChecks, roles: ["admin", "cleaner", "subcontractor"] },
  { to: "/app/linen", label: "Linnen", id: "linnen", icon: Shirt, roles: ["admin", "host"] },
  { to: "/app/billing", label: "Kosten & Facturatie", id: "kosten & facturatie", icon: Receipt, roles: ["admin", "client"] },
  { to: "/app/keys", label: "Sleutelbeheer", id: "sleutelbeheer", icon: KeyRound, roles: "keyaccess" },
  { to: "/app/templates", label: "Sjablonen", id: "sjablonen", icon: LayoutTemplate, roles: ["admin"] },
  { to: "/app/users", label: "Gebruikers", id: "gebruikers", icon: UsersIcon, roles: ["admin"] },
]);

const MobileMenuButton = ({ open, onClick }) => {
  const { data: n } = useNotifications();
  const total = (n.total || 0) + (n.chat_total || 0) + (n.reservations_pending || 0);
  return (
    <Button variant="ghost" size="icon" onClick={onClick} className="relative" data-testid="mobile-menu-btn">
      {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      {!open && total > 0 && (
        <span className="absolute -top-0.5 -right-0.5 grid place-items-center min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-white text-[10px] font-bold" data-testid="mobile-menu-badge">{total > 99 ? "99+" : total}</span>
      )}
    </Button>
  );
};

export default function DashboardLayout() {
  const { user, setUser, logout } = useAuth();
  const changeLang = async (language) => {
    try {
      await api.put("/auth/me/language", { language });
      setUser({ ...user, language });
    } catch { toast.error(tx("Taal wijzigen mislukt")); }
  };
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [houseCount, setHouseCount] = useState(null);

  const scopedStaff = ["cleaner", "subcontractor"].includes(user.role);
  const isOwner = user.role === "client";
  useEffect(() => {
    if (scopedStaff || isOwner) api.get("/houses").then(({ data }) => setHouseCount(data.length)).catch(() => setHouseCount(0));
  }, [scopedStaff, isOwner]);

  const items = NAV
    .filter((n) => n.roles === "all" || (n.roles === "keyaccess" ? (user.role === "admin" || user.key_access) : n.roles.includes(user.role)))
    .filter((n) => !(n.to === "/app/houses" && scopedStaff && houseCount === 0))
    .map((n) => (n.to === "/app/houses" && isOwner && houseCount === 1 ? { ...n, label: tx("Woning") } : n));

  // Host: volgorde linkermenu gelijk aan de dashboard-tegels.
  const HOST_ORDER = ["/app", "/app/calendar", "/app/chat", "/app/houses", "/app/keys", "/app/linen"];
  if (user.role === "host") {
    items.sort((a, b) => {
      const ia = HOST_ORDER.indexOf(a.to), ib = HOST_ORDER.indexOf(b.to);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
  }

  const doLogout = async () => {
    await forgetPush();
    setBadge(0);
    await logout();
    toast.success(tx("Uitgelogd"));
    navigate("/login");
  };

  const SidebarInner = () => {
    const { data: notif } = useNotifications();
    const unreadFor = (id) => id === "taken" ? notif.tasks_total : id === "agenda" ? notif.bookings_total : id === "live chat" ? notif.chat_total : id === "boekingen" ? notif.reservations_pending : 0;
    const navLabel = (n) => (n.to === "/app/billing" && user.role === "client") ? tx("Overzicht") : n.label;
    const navId = (n) => (n.to === "/app/billing" && user.role === "client") ? "overzicht" : n.to === "/app/houses" && isOwner && houseCount === 1 ? "woning" : n.id;
    return (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center px-5 border-b border-border">
        <NavLink to="/app" end onClick={() => setOpen(false)} className="transition-opacity hover:opacity-80" title={tx("Terug naar dashboard")} data-testid="sidebar-logo-link">
          <img src={LOGO_FULL} alt={tx("Costa Key")} className="h-11 w-auto object-contain" data-testid="sidebar-logo" />
        </NavLink>
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {items.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${navId(n)}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-accent hover:text-foreground"
              }`
            }
          >
            <n.icon className="h-[18px] w-[18px]" /> {navLabel(n)}
            {n.id === "live chat" && notif.chat_flame > 0 && (
              <Flame className="ml-auto h-4 w-4 text-orange-500 animate-pulse shrink-0" data-testid="nav-flame-chat" />
            )}
            {unreadFor(n.id) > 0 && (
              <span className={`${n.id === "live chat" && notif.chat_flame > 0 ? "ml-1" : "ml-auto"} grid place-items-center min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-white text-[10px] font-bold`} data-testid={`nav-badge-${n.id}`}>
                {unreadFor(n.id)}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-border">
        {user.role === "guest" && (
          <WhatsAppContact compact highlight text={tx("Voor dringende zaken zijn we het best bereikbaar via een WhatsApp-oproep:")} />
        )}
        <div className="flex items-center gap-3 px-2 py-2">
          <span className="grid place-items-center h-9 w-9 rounded-full bg-secondary/15 text-secondary font-display font-semibold">
            {user.name?.[0]?.toUpperCase() || "?"}
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate" data-testid="current-user-name">{user.name}</div>
            <div className="text-xs text-muted-foreground">{ROLE_LABELS[user.role]}</div>
          </div>
          <div className="relative shrink-0" title={tx("Taal")}>
            <Globe className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <select value={getLang()} onChange={(e) => changeLang(e.target.value)} aria-label={tx("Taal")}
              className="h-7 pl-6 pr-1 rounded-md border border-border bg-background text-xs uppercase text-muted-foreground hover:text-foreground focus:outline-none focus:ring-1 focus:ring-ring cursor-pointer"
              data-testid="ui-language-select">
              {UI_LANGS.map((l) => <option key={l.value} value={l.value}>{l.value.toUpperCase()}</option>)}
            </select>
          </div>
        </div>
        {user.role !== "guest" && <PushToggle />}
        <Button variant="ghost" onClick={doLogout} className="w-full justify-start gap-3 text-muted-foreground mt-1" data-testid="logout-btn">
          <LogOut className="h-[18px] w-[18px]" /> {tx("Uitloggen")}
        </Button>
      </div>
    </div>
    );
  };

  return (
    <NotificationsProvider>
    <div className="min-h-screen flex bg-background">
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-border bg-card flex-col fixed inset-y-0">
        <SidebarInner />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="relative w-64 bg-card border-r border-border"><SidebarInner /></div>
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="lg:hidden h-16 border-b border-border bg-card flex items-center justify-between px-4 sticky top-0 z-40">
          <div className="flex items-center gap-2">
            <NavLink to="/app" end onClick={() => setOpen(false)} title={tx("Terug naar dashboard")} data-testid="mobile-logo-link">
              <img src={LOGO_FULL} alt={tx("Costa Key")} className="h-10 w-auto object-contain" />
            </NavLink>
          </div>
          <MobileMenuButton open={open} onClick={() => setOpen(!open)} />
        </header>
        <main className="p-5 sm:p-8 max-w-7xl mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
      {user.role !== "guest" && <MobileOnboarding />}
    </NotificationsProvider>
  );
}
